import {
  DEFAULT_EXPORT_PADDING,
  DEFAULT_FONT_FAMILY,
  DEFAULT_FONT_SIZE,
  EDITOR_LS_KEYS,
  IMAGE_MIME_TYPES,
  randomId,
  viewportCoordsToSceneCoords,
} from "@excalidraw/common";

import type { MermaidConfig } from "@excalidraw/mermaid-to-excalidraw";
import type { MermaidToExcalidrawResult } from "@excalidraw/mermaid-to-excalidraw/dist/interfaces";

import type { NonDeletedExcalidrawElement } from "@excalidraw/element/types";

import { EditorLocalStorage } from "../../data/EditorLocalStorage";
import { canvasToBlob } from "../../data/blob";
import { t } from "../../i18n";
import { convertToExcalidrawElements, exportToCanvas } from "../../index";

import type {
  AppClassProperties,
  BinaryFileData,
  BinaryFiles,
  DataURL,
} from "../../types";
import type { ExcalidrawElement } from "@excalidraw/element/types";
import { newImageElement, newTextElement } from "@excalidraw/element";
import type { ValueOf } from "@excalidraw/common/utility-types";
import type { FileId } from "@excalidraw/element/types";

const resetPreview = ({
  canvasRef,
  setError,
}: {
  canvasRef: React.RefObject<HTMLDivElement | null>;
  setError: (error: Error | null) => void;
}) => {
  const canvasNode = canvasRef.current;

  if (!canvasNode) {
    return;
  }
  const parent = canvasNode.parentElement;
  if (!parent) {
    return;
  }
  parent.style.background = "";
  setError(null);
  canvasNode.replaceChildren();
};

export interface MermaidToExcalidrawLibProps {
  loaded: boolean;
  api: Promise<{
    parseMermaidToExcalidraw: (
      definition: string,
      config?: MermaidConfig,
    ) => Promise<MermaidToExcalidrawResult>;
  }>;
}

interface ConvertMermaidToExcalidrawFormatProps {
  canvasRef: React.RefObject<HTMLDivElement | null>;
  mermaidToExcalidrawLib: MermaidToExcalidrawLibProps;
  mermaidDefinition: string;
  setError: (error: Error | null) => void;
  data: React.MutableRefObject<{
    elements: readonly NonDeletedExcalidrawElement[];
    files: BinaryFiles | null;
  }>;
}

export const convertMermaidToExcalidraw = async ({
  canvasRef,
  mermaidToExcalidrawLib,
  mermaidDefinition,
  setError,
  data,
}: ConvertMermaidToExcalidrawFormatProps) => {
  const canvasNode = canvasRef.current;
  const parent = canvasNode?.parentElement;

  if (!canvasNode || !parent) {
    return;
  }

  if (!mermaidDefinition) {
    resetPreview({ canvasRef, setError });
    return;
  }

  try {
    const api = await mermaidToExcalidrawLib.api;

    let ret;
    try {
      ret = await api.parseMermaidToExcalidraw(mermaidDefinition);
    } catch (err: any) {
      ret = await api.parseMermaidToExcalidraw(
        mermaidDefinition.replace(/"/g, "'"),
      );
    }
    const { elements, files } = ret;
    setError(null);

    data.current = {
      elements: convertToExcalidrawElements(elements, {
        regenerateIds: true,
      }),
      files,
    };

    const canvas = await exportToCanvas({
      elements: data.current.elements,
      files: data.current.files,
      exportPadding: DEFAULT_EXPORT_PADDING,
      maxWidthOrHeight:
        Math.max(parent.offsetWidth, parent.offsetHeight) *
        window.devicePixelRatio,
    });
    // if converting to blob fails, there's some problem that will
    // likely prevent preview and export (e.g. canvas too big)
    try {
      await canvasToBlob(canvas);
    } catch (e: any) {
      if (e.name === "CANVAS_POSSIBLY_TOO_BIG") {
        throw new Error(t("canvasError.canvasTooBig"));
      }
      throw e;
    }
    parent.style.background = "var(--default-bg-color)";
    canvasNode.replaceChildren(canvas);
  } catch (err: any) {
    parent.style.background = "var(--default-bg-color)";
    if (mermaidDefinition) {
      setError(err);
    }

    throw err;
  }
};

export const saveMermaidDataToStorage = (mermaidDefinition: string) => {
  EditorLocalStorage.set(
    EDITOR_LS_KEYS.MERMAID_TO_EXCALIDRAW,
    mermaidDefinition,
  );
};

export const insertToEditor = ({
  app,
  data,
  text,
  shouldSaveMermaidDataToStorage,
}: {
  app: AppClassProperties;
  data: React.MutableRefObject<{
    elements: readonly NonDeletedExcalidrawElement[];
    files: BinaryFiles | null;
  }>;
  text?: string;
  shouldSaveMermaidDataToStorage?: boolean;
}) => {
  const { elements: newElements, files } = data.current;

  if (!newElements.length) {
    return;
  }

  app.addElementsFromPasteOrLibrary({
    elements: newElements,
    files,
    position: "center",
    fitToContent: true,
  });
  app.setOpenDialog(null);

  if (shouldSaveMermaidDataToStorage && text) {
    saveMermaidDataToStorage(text);
  }
};

export type EducationPromptInput = {
  topic: string;
  audience: string;
  format: "lesson" | "diagram" | "quiz";
  includeImage: boolean;
  guidance?: string;
};

export type OnEducationSubmitRetValue = {
  text?: string;
  imageDataURL?: string | null;
  error?: Error | null;
};

const loadImageDimensions = (
  dataURL: string,
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = (err) => reject(err);
    image.src = dataURL;
  });
};

export const insertEducationContent = async ({
  app,
  text,
  imageDataURL,
  mimeType = IMAGE_MIME_TYPES.png,
}: {
  app: AppClassProperties;
  text: string | null;
  imageDataURL: string | null;
  mimeType?: ValueOf<typeof IMAGE_MIME_TYPES>;
}) => {
  const elements: ExcalidrawElement[] = [];
  const files: BinaryFiles = {};

  const { x: centerX, y: centerY } = viewportCoordsToSceneCoords(
    {
      clientX: app.state.width / 2 + app.state.offsetLeft,
      clientY: app.state.height / 2 + app.state.offsetTop,
    },
    app.state,
  );

  if (text?.trim()) {
    elements.push(
      newTextElement({
        x: centerX,
        y: centerY - 80,
        text: text.trim(),
        fontFamily: DEFAULT_FONT_FAMILY,
        fontSize: DEFAULT_FONT_SIZE,
        textAlign: "center",
        verticalAlign: "middle",
      }),
    );
  }

  if (imageDataURL) {
    const fileId = randomId() as FileId;
    const { width, height } = await loadImageDimensions(imageDataURL);
    const maxSize = 420;
    const scale = Math.min(1, maxSize / Math.max(width, height));
    const scaledWidth = width * scale || maxSize;
    const scaledHeight = height * scale || maxSize;

    const fileData: BinaryFileData = {
      mimeType,
      id: fileId,
      dataURL: imageDataURL as DataURL,
      created: Date.now(),
      lastRetrieved: Date.now(),
    };
    files[fileId] = fileData;

    elements.push(
      newImageElement({
        type: "image",
        x: centerX - scaledWidth / 2,
        y: centerY + (text ? 40 : -scaledHeight / 2),
        width: scaledWidth,
        height: scaledHeight,
        fileId,
        status: "pending",
      }),
    );
  }

  if (!elements.length) {
    return;
  }

  app.addElementsFromPasteOrLibrary({
    elements,
    files: Object.keys(files).length ? files : null,
    position: "center",
    fitToContent: true,
  });
};
